/**
 * Module-access permissions — frontend gating (access overhaul Stage 3;
 * AGENTS.md "Multi-tenancy & access control").
 *
 * The source of truth is the server-computed `membership.prava` from
 * /v1/auth/me: an effective level per module ('none' | 'read' | 'write' |
 * 'admin'), with the zakaznici implication already applied server-side
 * (api/src/services/access.ts). This file only *mirrors* it for UX — the API
 * is the hard boundary.
 *
 * Rules:
 *   - a module is visible/navigable at `read`+;
 *   - Přehled is the only baseline left for every member (D1 refined by
 *     Stage 7: Úkoly is a managed module now — migration 0010 backfilled
 *     `ukoly: write` for every membership, so nobody lost tasks until an
 *     admin tightens the grant);
 *   - Management (Stage 5: the company-admin "Správa" area) is visible to
 *     company admins (`membership.spravce`) and platform admins;
 *   - mutation affordances ask `can(modul, 'write')`, module settings
 *     `can(modul, 'admin')` (Stage 4/5 adopt this module by module).
 */

import { useMemo } from 'react';
import type { NavId } from '@/components/AppRail';
import { useMeQuery, type MeMembership } from '@/modules/dashboard/api';

export type Modul =
  | 'kontakty'
  | 'vozidla'
  | 'ojeta_vozidla'
  | 'leasing'
  | 'zakaznici'
  | 'servis'
  | 'posta'
  | 'kalendar'
  | 'ukoly'
  | 'uskladneni'
  | 'pujcovna';

export type Uroven = 'none' | 'read' | 'write' | 'admin';
export type NeedUroven = Exclude<Uroven, 'none'>;

const RANK: Record<Uroven, number> = { none: 0, read: 1, write: 2, admin: 3 };

/** Nav ids of module workspaces → their access module. */
const NAV_MODUL: Partial<Record<NavId, Modul>> = {
  kontakty: 'kontakty',
  vozidla: 'vozidla',
  ojeta: 'ojeta_vozidla',
  leasing: 'leasing',
  zakaznici: 'zakaznici',
  servis: 'servis',
  posta: 'posta',
  kalendar: 'kalendar',
  ukoly: 'ukoly',
  pujcovna: 'pujcovna',
};

export interface AccessInput {
  membership: MeMembership | null | undefined;
  /** `uzivatele.admin` — platform admin; sees Management everywhere. */
  admin: boolean;
}

/** The user's effective level for a module ('none' when unknown). */
export function moduleLevel(m: MeMembership | null | undefined, modul: Modul): Uroven {
  return m?.prava?.[modul] ?? 'none';
}

/** `true` when the user's effective level reaches `need`. */
export function canLevel(
  m: MeMembership | null | undefined,
  modul: Modul,
  need: NeedUroven,
): boolean {
  return RANK[moduleLevel(m, modul)] >= RANK[need];
}

/**
 * The set of nav modules a user may see/visit. Empty when membership hasn't
 * loaded — callers must treat "empty" as "unknown / don't redirect yet".
 */
export function visibleModules({ membership: m, admin }: AccessInput): Set<NavId> {
  const v = new Set<NavId>();
  if (!m) return v;

  // Baseline for every member (D1, refined by Stage 7): the dashboard only —
  // Úkoly follows its grant like every other module since migration 0010.
  v.add('prehled');

  for (const [nav, modul] of Object.entries(NAV_MODUL) as Array<[NavId, Modul]>) {
    if (canLevel(m, modul, 'read')) v.add(nav);
  }

  // Stage 8: uskladněni (tyre storage) is its own managed module but has no
  // rail item — it lives under the Servis workspace (/servis/uskladneni), so
  // the servis nav id lights up for uskladněni-only members too. NAV_MODUL
  // stays 1:1; this is the single deliberate exception.
  if (canLevel(m, 'uskladneni', 'read')) v.add('servis');

  // Správa firmy (Stage 5): the company-admin area — visible to the
  // company's správce and to platform admins (who service every dealership).
  if (m.spravce === 1 || admin) {
    v.add('management');
  }
  // Platforma: platform administration across all companies — admins only.
  if (admin) v.add('platforma');
  return v;
}

/**
 * Maps a `/...` pathname to the module that gates it (for filtering the
 * command palette + any other route-keyed surface). Returns null for paths
 * that aren't module-gated (then the caller should leave them visible).
 */
export function pathModule(path: string): NavId | null {
  const p = path.split('?')[0];
  if (p === '/' || p === '/') return 'prehled';
  if (p.startsWith('/ukoly')) return 'ukoly';
  if (p.startsWith('/kontakty')) return 'kontakty';
  if (p.startsWith('/vozidla')) return 'vozidla';
  if (p.startsWith('/ojeta-vozidla')) return 'ojeta';
  if (p.startsWith('/leasing')) return 'leasing';
  if (p.startsWith('/zakaznici')) return 'zakaznici';
  if (p.startsWith('/servis')) return 'servis';
  if (p.startsWith('/posta')) return 'posta';
  if (p.startsWith('/kalendar')) return 'kalendar';
  if (p.startsWith('/pujcovna')) return 'pujcovna';
  if (p.startsWith('/management')) return 'management';
  if (p.startsWith('/platforma')) return 'platforma';
  return null;
}

/** Landing path for a user, given their accessible modules. */
export function homeModulePath(access: Set<NavId>): string {
  if (access.has('prehled')) return '/';
  if (access.has('servis')) return '/servis/planovani';
  // No known-safe home (e.g. membership with no modules) — fall back to the
  // dashboard; the page-level guard skips redirecting on an empty set so this
  // never loops.
  return '/';
}

export interface ModuleAccess {
  /** Modules the user may see. Empty while membership is still loading. */
  access: Set<NavId>;
  /** True once we know the answer (me query settled with a membership). */
  resolved: boolean;
  canAccess: (id: NavId) => boolean;
  /** Level check for mutation/settings affordances (Stage 4+). */
  can: (modul: Modul, need: NeedUroven) => boolean;
  /** Company admin (firmy_uzivatele.spravce): manages members + grants. */
  isSpravce: boolean;
  /** Platform admin (uzivatele.admin). */
  isAdmin: boolean;
  /** The other firmas of the active firma's group (transfer targets); empty without a group. */
  skupinaFirmy: { id_firmy: number; Nazev: string; ojeta_vozidla?: boolean; vozidla?: boolean }[];
  homePath: string;
}

const NO_FIRMY: { id_firmy: number; Nazev: string; ojeta_vozidla?: boolean; vozidla?: boolean }[] = [];

/** Hook wrapper over `visibleModules` keyed off `useMeQuery`. */
export function useModuleAccess(): ModuleAccess {
  const me = useMeQuery();
  return useMemo(() => {
    const membership = me.data?.membership;
    const admin = me.data?.user.admin ?? false;
    const access = visibleModules({ membership, admin });
    const resolved = !me.isLoading && me.data != null && membership != null;
    return {
      access,
      resolved,
      canAccess: (id: NavId) => access.has(id),
      can: (modul: Modul, need: NeedUroven) => canLevel(membership, modul, need),
      isSpravce: membership?.spravce === 1,
      isAdmin: admin,
      skupinaFirmy: membership?.skupina_firmy ?? NO_FIRMY,
      homePath: homeModulePath(access),
    };
  }, [me.data, me.isLoading]);
}
